import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { MessageSquare, Send, User, Shield, Users, ChevronLeft, Search, Check, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentMessages = () => {
  const { user, getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  const [activeTab, setActiveTab] = useState('coach'); // 'coach' | 'manager'

  // Coach conversation (existing flow)
  const [conversation, setConversation] = useState(null);
  const [coachMessages, setCoachMessages] = useState([]);
  const [coachDraft, setCoachDraft] = useState('');
  const [coachLoading, setCoachLoading] = useState(true);
  const [coachSending, setCoachSending] = useState(false);

  // Manager threads
  const [mgrThreads, setMgrThreads] = useState([]);
  const [mgrLoading, setMgrLoading] = useState(true);
  const [activeMgr, setActiveMgr] = useState(null); // { team_id, manager_id, ... }
  const [mgrMessages, setMgrMessages] = useState([]);
  const [mgrDraft, setMgrDraft] = useState('');
  const [mgrSending, setMgrSending] = useState(false);
  const [mgrTyping, setMgrTyping] = useState(false);
  const typingPollRef = useRef(null);
  const typingDebounceRef = useRef(null);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (athlete) loadCoachConversation();
  }, [athlete]); // eslint-disable-line

  useEffect(() => {
    loadManagerThreads();
  }, []); // eslint-disable-line

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [coachMessages.length, mgrMessages.length]);

  // Manager typing indicator — poll every 2s while a thread is open
  useEffect(() => {
    if (!activeMgr) {
      setMgrTyping(false);
      return;
    }
    const poll = async () => {
      try {
        const res = await axios.get(
          `${API}/clubs/parent/team/${activeMgr.team_id}/manager/${activeMgr.manager_id}/typing`,
          { headers: getAuthHeaders() }
        );
        setMgrTyping(!!res.data?.typing);
      } catch (e) {
        // silent
      }
    };
    poll();
    typingPollRef.current = setInterval(poll, 2500);
    return () => {
      if (typingPollRef.current) clearInterval(typingPollRef.current);
    };
  }, [activeMgr]); // eslint-disable-line

  const loadCoachConversation = async () => {
    if (!athlete) return;
    setCoachLoading(true);
    try {
      const convRes = await axios.get(`${API}/conversations/athlete/${athlete.id}`, { headers: getAuthHeaders() });
      setConversation(convRes.data);
      const msgRes = await axios.get(`${API}/conversations/${convRes.data.id}/messages`, { headers: getAuthHeaders() });
      setCoachMessages(msgRes.data);
    } catch (error) {
      // graceful — coach conv may not exist for independent athletes
    }
    setCoachLoading(false);
  };

  const loadManagerThreads = async () => {
    setMgrLoading(true);
    try {
      const res = await axios.get(`${API}/clubs/parent/manager-threads`, { headers: getAuthHeaders() });
      setMgrThreads(res.data || []);
    } catch (error) {
      // graceful — manager threads may not exist
    }
    setMgrLoading(false);
  };

  const openManagerThread = async (thread) => {
    setActiveMgr(thread);
    setMgrMessages([]);
    try {
      const res = await axios.get(
        `${API}/clubs/parent/team/${thread.team_id}/manager/${thread.manager_id}/messages`,
        { headers: getAuthHeaders() }
      );
      setMgrMessages(res.data || []);
      // refresh unread badge in the inbox
      loadManagerThreads();
    } catch (e) {
      toast.error('Failed to load thread');
    }
  };

  const sendCoachMessage = async (e) => {
    e.preventDefault();
    if (!coachDraft.trim() || !conversation) return;
    setCoachSending(true);
    try {
      const res = await axios.post(
        `${API}/conversations/${conversation.id}/messages`,
        { message_text: coachDraft },
        { headers: getAuthHeaders() }
      );
      setCoachMessages([...coachMessages, res.data]);
      setCoachDraft('');
    } catch (error) {
      toast.error('Failed to send message');
    }
    setCoachSending(false);
  };

  const pingManagerTyping = () => {
    if (!activeMgr) return;
    if (typingDebounceRef.current) return;
    typingDebounceRef.current = setTimeout(() => {
      typingDebounceRef.current = null;
    }, 1500);
    axios.post(
      `${API}/clubs/parent/team/${activeMgr.team_id}/manager/${activeMgr.manager_id}/typing`,
      {},
      { headers: getAuthHeaders() }
    ).catch(() => {});
  };

  const sendManagerMessage = async (e) => {
    e.preventDefault();
    if (!mgrDraft.trim() || !activeMgr || mgrSending) return;
    setMgrSending(true);
    try {
      const res = await axios.post(
        `${API}/clubs/parent/team/${activeMgr.team_id}/manager/${activeMgr.manager_id}/messages`,
        { body: mgrDraft },
        { headers: getAuthHeaders() }
      );
      setMgrMessages([...mgrMessages, res.data]);
      setMgrDraft('');
    } catch (e) {
      toast.error('Failed to send');
    }
    setMgrSending(false);
  };

  const formatMessageTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, h:mm a');
    } catch {
      return '';
    }
  };

  const totalUnreadMgr = mgrThreads.reduce((sum, t) => sum + (t.unread_count || 0), 0);

  return (
    <div className="space-y-4" data-testid="parent-messages">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Messages</h1>
        <p className="text-muted-foreground">
          Coach and team manager conversations
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/40 p-1 rounded-sm w-fit">
        <button
          type="button"
          onClick={() => setActiveTab('coach')}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-sm transition ${
            activeTab === 'coach' ? 'bg-primary text-black font-medium' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="parent-msg-tab-coach"
        >
          <Shield size={14} /> Coach
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('manager')}
          className={`relative flex items-center gap-2 px-4 py-2 text-sm rounded-sm transition ${
            activeTab === 'manager' ? 'bg-primary text-black font-medium' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="parent-msg-tab-manager"
        >
          <Users size={14} /> Team Manager
          {totalUnreadMgr > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
              {totalUnreadMgr}
            </span>
          )}
        </button>
      </div>

      {/* COACH TAB */}
      {activeTab === 'coach' && (
        <CoachThread
          loading={coachLoading}
          conversation={conversation}
          messages={coachMessages}
          formatMessageTime={formatMessageTime}
          userId={user?.id}
          draft={coachDraft}
          setDraft={setCoachDraft}
          sending={coachSending}
          onSend={sendCoachMessage}
          messagesEndRef={messagesEndRef}
          athleteName={athlete?.name}
        />
      )}

      {/* MANAGER TAB */}
      {activeTab === 'manager' && (
        <div>
          {!activeMgr ? (
            <ManagerInbox
              loading={mgrLoading}
              threads={mgrThreads}
              onSelect={openManagerThread}
            />
          ) : (
            <ManagerThread
              thread={activeMgr}
              messages={mgrMessages}
              userId={user?.id}
              draft={mgrDraft}
              setDraft={(v) => {
                setMgrDraft(v);
                pingManagerTyping();
              }}
              sending={mgrSending}
              typing={mgrTyping}
              onSend={sendManagerMessage}
              onBack={() => {
                setActiveMgr(null);
                loadManagerThreads();
              }}
              formatMessageTime={formatMessageTime}
              messagesEndRef={messagesEndRef}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ============ Sub-components ============

const CoachThread = ({ loading, conversation, messages, formatMessageTime, userId, draft, setDraft, sending, onSend, messagesEndRef, athleteName }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!conversation) {
    return (
      <div className="bg-card border border-border/40 rounded-sm p-10 text-center text-muted-foreground">
        <Shield className="mx-auto mb-3 opacity-50" size={32} />
        <p>No coach conversation yet for {athleteName || 'this athlete'}.</p>
      </div>
    );
  }
  return (
    <div className="bg-card border border-border/40 rounded-sm flex flex-col h-[500px]">
      <div className="px-4 py-3 border-b border-border/40 text-sm text-muted-foreground">
        Athlete: <span className="text-foreground font-medium">{conversation.athlete_name}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="coach-msg-list">
        {messages.length === 0 ? (
          <EmptyChat label={`Start the conversation with ${athleteName}'s coach`} />
        ) : (
          messages.map((msg) => (
            <Bubble
              key={msg.id}
              text={msg.message_text}
              isOwn={msg.sender_id === userId}
              senderName={msg.sender_name}
              senderRole={msg.sender_role}
              time={formatMessageTime(msg.created_at)}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={onSend} className="p-4 border-t border-border/40 flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type your message…"
          className="flex-1"
          disabled={sending}
          data-testid="coach-msg-input"
        />
        <Button type="submit" disabled={sending || !draft.trim()} className="bg-primary text-black" data-testid="coach-msg-send">
          <Send size={18} />
        </Button>
      </form>
    </div>
  );
};

const ManagerInbox = ({ loading, threads, onSelect }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (threads.length === 0) {
    return (
      <div className="bg-card border border-border/40 rounded-sm p-10 text-center text-muted-foreground">
        <Users className="mx-auto mb-3 opacity-50" size={32} />
        <p>No team manager threads yet.</p>
        <p className="text-xs mt-1 opacity-70">
          Your team's manager can message you here for game logistics, RSVPs, and tournament info.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-card border border-border/40 rounded-sm divide-y divide-border/40">
      {threads.map((t) => (
        <button
          key={`${t.team_id}-${t.manager_id}`}
          onClick={() => onSelect(t)}
          className="w-full text-left p-4 hover:bg-muted/40 transition flex items-center gap-3"
          data-testid={`manager-thread-${t.manager_id}`}
        >
          <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold">
            {(t.manager_name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium truncate">{t.manager_name}</p>
              {t.last_message_at && (
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {format(parseISO(t.last_message_at), 'MMM d')}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {t.team_name} • {t.last_message
                ? `${t.last_sender_role === 'manager' ? '' : 'You: '}${t.last_message}`
                : 'No messages yet'}
            </p>
          </div>
          {t.unread_count > 0 && (
            <span className="bg-primary text-black text-[10px] font-bold rounded-full px-2 py-0.5">
              {t.unread_count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

const ManagerThread = ({ thread, messages, userId, draft, setDraft, sending, typing, onSend, onBack, formatMessageTime, messagesEndRef }) => {
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const filtered = search ? messages.filter((m) => (m.body || '').toLowerCase().includes(search.toLowerCase())) : messages;
  return (
    <div className="bg-card border border-border/40 rounded-sm flex flex-col h-[500px]">
      <div className="px-4 py-3 border-b border-border/40 flex items-center gap-3">
        <button
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground p-1 -ml-1"
          data-testid="manager-thread-back"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{thread.manager_name}</p>
          <p className="text-xs text-muted-foreground truncate">{thread.team_name} • Team Manager</p>
        </div>
        <button
          onClick={() => setShowSearch((s) => !s)}
          className="text-muted-foreground hover:text-foreground p-1.5"
          title="Search"
          data-testid="parent-thread-search-toggle"
        >
          <Search size={16} />
        </button>
      </div>

      {showSearch && (
        <div className="px-4 py-2 border-b border-border/40 bg-muted/20">
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search messages…"
            className="h-8 text-sm"
            data-testid="parent-thread-search-input"
          />
          {search && (
            <p className="text-[10px] text-muted-foreground mt-1">
              {filtered.length} match(es)
            </p>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="manager-thread-list">
        {filtered.length === 0 && !search ? (
          <EmptyChat label={`Start the conversation with ${thread.manager_name}`} />
        ) : filtered.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-10">No matches for "{search}"</div>
        ) : (
          filtered.map((m) => (
            <Bubble
              key={m.id}
              text={m.body}
              isOwn={m.sender_id === userId}
              senderName={m.sender_name}
              senderRole={m.sender_role}
              time={formatMessageTime(m.created_at)}
              readByOther={m.sender_id === userId ? !!m.read_by_manager : undefined}
              highlight={search}
            />
          ))
        )}
        {typing && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="bg-muted rounded-lg px-3 py-2 text-xs text-muted-foreground inline-flex items-center gap-2">
              <span className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
              {thread.manager_name?.split(' ')[0] || 'Manager'} is typing…
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={onSend} className="p-4 border-t border-border/40 flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`Message ${thread.manager_name?.split(' ')[0] || 'manager'}…`}
          className="flex-1"
          disabled={sending}
          data-testid="manager-thread-input"
        />
        <Button type="submit" disabled={sending || !draft.trim()} className="bg-primary text-black" data-testid="manager-thread-send">
          <Send size={18} />
        </Button>
      </form>
    </div>
  );
};

const Bubble = ({ text, isOwn, senderName, senderRole, time, readByOther, highlight }) => {
  let rendered = text;
  if (highlight && text && text.toLowerCase().includes(highlight.toLowerCase())) {
    const idx = text.toLowerCase().indexOf(highlight.toLowerCase());
    rendered = (
      <>
        {text.slice(0, idx)}
        <mark className="bg-amber-400/40 text-foreground rounded px-0.5">
          {text.slice(idx, idx + highlight.length)}
        </mark>
        {text.slice(idx + highlight.length)}
      </>
    );
  }
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
        {!isOwn && (
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
              <User size={12} />
            </div>
            <span className="text-xs text-muted-foreground">
              {senderName}{senderRole ? ` (${senderRole})` : ''}
            </span>
          </div>
        )}
        <div className={`px-4 py-2 rounded-lg ${isOwn ? 'bg-primary text-black' : 'bg-muted'}`}>
          <p className="text-sm whitespace-pre-wrap">{rendered}</p>
        </div>
        <p className={`flex items-center gap-1 text-xs text-muted-foreground mt-1 ${isOwn ? 'justify-end' : ''}`}>
          <span>{time}</span>
          {isOwn && (readByOther !== undefined) && (
            readByOther
              ? <CheckCheck size={11} title="Read" className="text-primary" />
              : <Check size={11} title="Sent" />
          )}
        </p>
      </div>
    </div>
  );
};

const EmptyChat = ({ label }) => (
  <div className="flex flex-col items-center justify-center h-full text-center">
    <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
    <p className="text-muted-foreground">No messages yet</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

export default ParentMessages;
